import React, { createContext, useContext, useState, useEffect } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useAuth } from './AuthContext';
import { Categoria } from '@/types/app';
import { categoriasPadrao } from '@/constants/categorias';
import { logger } from '@/utils/logger';

interface CategoriaContextType {
  categoriasCustomizadas: Categoria[];
  adicionarCategoria: (categoria: Categoria) => Promise<void>;
  removerCategoria: (categoriaId: string) => Promise<void>;
  todasCategorias: Categoria[];
  loading: boolean;
}

const CategoriaContext = createContext<CategoriaContextType | undefined>(undefined);

export function CategoriaProvider({ children }: { children: React.ReactNode }) {
  const [categoriasCustomizadas, setCategoriasCustomizadas] = useState<Categoria[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      loadCategoriasCustomizadas();
    }
  }, [user]);

  const todasCategorias = [...categoriasPadrao, ...categoriasCustomizadas];

  const loadCategoriasCustomizadas = async () => {
    try {
      const storedCategorias = await AsyncStorage.getItem(`@categorias_customizadas_${user?.id}`);
      if (storedCategorias) {
        setCategoriasCustomizadas(JSON.parse(storedCategorias));
      }
    } catch (error) {
      logger.error('Error loading custom categories', { error });
    } finally {
      setLoading(false);
    }
  };

  const saveCategoriasCustomizadas = async (categorias: Categoria[]) => {
    try {
      await AsyncStorage.setItem(`@categorias_customizadas_${user?.id}`, JSON.stringify(categorias));
    } catch (error) {
      logger.error('Error saving custom categories', { error });
      throw error;
    }
  };

  const adicionarCategoria = async (categoria: Categoria) => {
    try {
      const novasCategorias = [...categoriasCustomizadas, categoria];
      setCategoriasCustomizadas(novasCategorias);
      await saveCategoriasCustomizadas(novasCategorias);
    } catch (error) {
      logger.error('Error adding category', { error });
      throw error;
    }
  };

  const removerCategoria = async (categoriaId: string) => {
    try {
      const novasCategorias = categoriasCustomizadas.filter(c => c.id !== categoriaId);
      setCategoriasCustomizadas(novasCategorias);
      await saveCategoriasCustomizadas(novasCategorias);
    } catch (error) {
      logger.error('Error removing category', { error });
      throw error;
    }
  };

  return (
    <CategoriaContext.Provider value={{
      categoriasCustomizadas,
      adicionarCategoria,
      removerCategoria,
      todasCategorias,
      loading
    }}>
      {children}
    </CategoriaContext.Provider>
  );
}

export function useCategoria() {
  const context = useContext(CategoriaContext);
  if (context === undefined) {
    throw new Error('useCategoria must be used within a CategoriaProvider');
  }
  return context;
}